import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import Layout from '../Layout'
import { formatCurrency } from '../../lib/format'

interface ImageFile {
  id: string
  url: string
  filename: string
  content_type: string
  byte_size: number
}

interface ProductOptionValue {
  id: number
  value: string
  position: number
}

interface ProductOption {
  id: number
  name: string
  position: number
  product_option_values: ProductOptionValue[]
}

interface Product {
  id: number
  name: string
  sku: string
  price: number
  track_variants: boolean
  product_images?: ImageFile[]
  tax_rate: number
  unit_type: number
  track_inventory: boolean
  requires_serial: boolean
  max_web_inventory: number | null
}

interface Variant {
  id: number
  sku: string
  price: number
  stock_quantity: number
  active: boolean
  option_display: string
  variant_images?: ImageFile[]
}

interface Account {
  id: number
  name: string
  slug: string
}

interface Props {
  product: Product
  variants: Variant[]
  product_options: ProductOption[]
  account: Account
  can_manage?: boolean
}

const UNIT_TYPE_LABELS: Record<number, string> = {
  0: 'Unidad',
  1: 'Peso',
  2: 'Metro',
}

export default function VariantsIndex({ product, variants: initialVariants, product_options: existingOptions, account }: Props) {
  const [search, setSearch] = useState('')

  // Option creation state
  const [options, setOptions] = useState<Array<{ name: string; values: string[] }>>(
    existingOptions.map(po => ({
      name: po.name,
      values: po.product_option_values.sort((a, b) => a.position - b.position).map(v => v.value)
    }))
  )
  const [newOptionName, setNewOptionName] = useState('')
  const [newOptionValues, setNewOptionValues] = useState('')
  const [showNewOption, setShowNewOption] = useState(false)
  const [creatingVariants, setCreatingVariants] = useState(false)
  const [previewVariants, setPreviewVariants] = useState<string[]>([])

  // Variant inline editing
  const [editingVariantId, setEditingVariantId] = useState<number | null>(null)
  const [editPrice, setEditPrice] = useState('')
  const [editStock, setEditStock] = useState('')
  const [editActive, setEditActive] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState<number | null>(null)
  const [uploadingImageFor, setUploadingImageFor] = useState<number | null>(null)
  const [deletingImage, setDeletingImage] = useState<string | null>(null)

  const filtered = initialVariants.filter(v =>
    v.option_display.toLowerCase().includes(search.toLowerCase()) ||
    v.sku.toLowerCase().includes(search.toLowerCase())
  )

  // Option management
  const handleAddOption = () => {
    const trimmedName = newOptionName.trim()
    const values = newOptionValues.split(',').map(v => v.trim()).filter(v => v.length > 0)
    if (!trimmedName || values.length === 0) return
    setOptions([...options, { name: trimmedName, values }])
    setNewOptionName('')
    setNewOptionValues('')
    setShowNewOption(false)
    setPreviewVariants([])
  }

  const handleRemoveOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index))
    setPreviewVariants([])
  }

  // Preview
  const handlePreview = () => {
    if (options.length === 0) return
    const cartesian = (arrs: string[][]) => arrs.reduce((a, b) => a.flatMap(x => b.map(y => [...x, y])), [[]] as string[][])
    const combos = cartesian(options.map(o => o.values))
    setPreviewVariants(combos.map(c => c.join(' / ')))
  }

  // Generate variants
  const handleGenerateVariants = () => {
    if (options.length === 0) return
    setCreatingVariants(true)

    const formData = new FormData()
    formData.append('options', JSON.stringify(options))

    router.post(`/${account.slug}/products/${product.id}/variants/create_options`, formData, {
      onSuccess: () => setCreatingVariants(false),
      onError: () => setCreatingVariants(false),
    })
  }

  // Variant editing
  const startEdit = (v: Variant) => {
    setEditingVariantId(v.id)
    setEditPrice(v.price.toString())
    setEditStock(v.stock_quantity.toString())
    setEditActive(v.active)
  }

  const cancelEdit = () => setEditingVariantId(null)

  const saveEdit = (variantId: number) => {
    setSaving(true)
    const formData = new FormData()
    formData.append('variant[price]', editPrice)
    formData.append('variant[stock_quantity]', editStock)
    formData.append('variant[active]', editActive.toString())

    router.put(`/${account.slug}/products/${product.id}/variants/${variantId}`, formData, {
      onSuccess: () => { setSaving(false); setEditingVariantId(null) },
      onError: () => setSaving(false),
    })
  }

  const handleDelete = (variantId: number) => {
    if (!confirm('¿Eliminar esta variante?')) return
    setDeleting(variantId)
    router.delete(`/${account.slug}/products/${product.id}/variants/${variantId}`, {
      onSuccess: () => setDeleting(null),
      onError: () => setDeleting(null),
    })
  }

  const handleAttachImages = (variantId: number, files: FileList | null) => {
    if (!files || files.length === 0) return
    setUploadingImageFor(variantId)
    const formData = new FormData()
    formData.append('images[]', files[0])
    router.post(`/${account.slug}/products/${product.id}/variants/${variantId}/attach_image`, formData, {
      onSuccess: () => { setUploadingImageFor(null); router.reload() },
      onError: () => setUploadingImageFor(null),
    })
  }

  const handleDeleteImage = (variantId: number, imageId: string) => {
    setDeletingImage(imageId)
    router.delete(`/${account.slug}/products/${product.id}/variants/${variantId}/destroy_image`, {
      data: { image_signed_id: imageId },
      onSuccess: () => { setDeletingImage(null); router.reload() },
      onError: () => setDeletingImage(null),
    })
  }

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const totalStock = initialVariants.reduce((sum, v) => sum + v.stock_quantity, 0)
  const activeCount = initialVariants.filter(v => v.active).length

  return (
    <Layout account={account}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link href={`/${account.slug}/products/${product.id}/edit`} className="text-[#55ACEE] hover:underline text-sm">
              ← {product.name}
            </Link>
            <h1 className="text-2xl font-bold text-[#202020]">Variantes</h1>
            <p className="text-sm text-gray-500 mt-1">
              {initialVariants.length} variantes · {activeCount} activas · Stock total: {totalStock}
            </p>
          </div>
          <Link href={`/${account.slug}/products/${product.id}/edit`} className="bg-gray-200 text-gray-700 px-3 py-1.5 rounded text-sm hover:bg-gray-300 transition-colors">
            Editar producto
          </Link>
        </div>

        {/* ℹ️ Heredado del producto */}
        <div className="bg-white p-4 rounded-lg shadow mb-6 border-l-4 border-[#55ACEE]">
          <p className="text-sm font-medium text-[#202020] mb-3">ℹ️ Heredado del producto</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 text-sm">
            <div>
              <span className="text-gray-500">ISV:</span>{' '}
              <span className="font-medium text-[#202020]">{product.tax_rate ?? 0}%</span>
            </div>
            <div>
              <span className="text-gray-500">Unidad:</span>{' '}
              <span className="font-medium text-[#202020]">{UNIT_TYPE_LABELS[product.unit_type] || 'Unidad'}</span>
            </div>
            <div>
              <span className="text-gray-500">Rebaja inventario:</span>{' '}
              <span className={`font-medium ${product.track_inventory ? 'text-green-600' : 'text-gray-400'}`}>
                {product.track_inventory ? 'Sí' : 'No'}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Requiere serial:</span>{' '}
              <span className={`font-medium ${product.requires_serial ? 'text-green-600' : 'text-gray-400'}`}>
                {product.requires_serial ? 'Sí' : 'No'}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Tope web:</span>{' '}
              <span className="font-medium text-[#202020]">
                {product.max_web_inventory != null ? product.max_web_inventory : 'Sin límite'}
              </span>
            </div>
          </div>
        </div>

        {/* No options yet → show setup panel */}
        {options.length === 0 && initialVariants.length === 0 && (
          <div className="bg-white p-6 rounded-lg shadow space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-[#202020] mb-2">Configurar opciones del producto</h2>
              <p className="text-sm text-gray-500">Agrega opciones (ej: Talla, Color) y sus valores para generar las variantes automáticamente.</p>
            </div>

            {showNewOption && (
              <div className="bg-gray-50 p-4 rounded space-y-3">
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de opción</label>
                    <input type="text" value={newOptionName} onChange={(e) => setNewOptionName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]" placeholder="Ej: Talla, Color" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Valores (separados por coma)</label>
                    <input type="text" value={newOptionValues} onChange={(e) => setNewOptionValues(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]" placeholder="Ej: S, M, L, XL" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={handleAddOption} className="bg-[#55ACEE] text-white px-3 py-1.5 rounded text-sm hover:bg-[#4a9dd4] transition-colors">Agregar</button>
                  <button type="button" onClick={() => { setShowNewOption(false); setNewOptionName(''); setNewOptionValues('') }} className="text-gray-500 hover:text-gray-700 text-sm px-3 py-1.5">Cancelar</button>
                </div>
              </div>
            )}

            <button type="button" onClick={() => setShowNewOption(true)} className="bg-[#FFC107] text-[#202020] px-4 py-2 rounded font-semibold hover:bg-[#e6ad06] transition-colors">
              + Agregar primera opción
            </button>
          </div>
        )}

        {/* Options already defined → show options panel + preview + variants table */}
        {(options.length > 0 || initialVariants.length > 0) && (
          <div className="space-y-6">
            {/* Options config panel */}
            {options.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow space-y-4">
              <h2 className="text-lg font-semibold text-[#202020]">Opciones configuradas ({options.length}/2)</h2>

              <div className="space-y-2">
                {options.map((opt, i) => (
                  <div key={i} className="flex items-center gap-3 bg-gray-50 p-3 rounded">
                    <div className="flex-1">
                      <span className="font-medium text-[#202020]">{opt.name}:</span>{' '}
                      {opt.values.map((v, vi) => (
                        <span key={vi} className="inline-block bg-[#55ACEE] text-white text-xs px-2 py-0.5 rounded mr-1">{v}</span>
                      ))}
                    </div>
                    <button type="button" onClick={() => handleRemoveOption(i)} className="text-red-500 hover:text-red-700 text-sm">✕</button>
                  </div>
                ))}
              </div>

              {options.length < 2 && !showNewOption && (
                <button type="button" onClick={() => setShowNewOption(true)} className="text-[#55ACEE] hover:underline text-sm font-medium">+ Agregar otra opción</button>
              )}
              {showNewOption && (
                <div className="bg-gray-50 p-4 rounded space-y-3">
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de opción</label>
                      <input type="text" value={newOptionName} onChange={(e) => setNewOptionName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]" placeholder="Ej: Talla, Color" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Valores (separados por coma)</label>
                      <input type="text" value={newOptionValues} onChange={(e) => setNewOptionValues(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]" placeholder="Ej: S, M, L, XL" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button type="button" onClick={handleAddOption} className="bg-[#55ACEE] text-white px-3 py-1.5 rounded text-sm hover:bg-[#4a9dd4] transition-colors">Agregar</button>
                    <button type="button" onClick={() => { setShowNewOption(false); setNewOptionName(''); setNewOptionValues('') }} className="text-gray-500 hover:text-gray-700 text-sm px-3 py-1.5">Cancelar</button>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 pt-2 border-t border-gray-200">
                <button type="button" onClick={handlePreview} className="bg-gray-200 text-gray-700 px-3 py-1.5 rounded text-sm hover:bg-gray-300 transition-colors">
                  👁 Vista previa
                </button>
                <button
                  type="button"
                  onClick={handleGenerateVariants}
                  disabled={creatingVariants}
                  className="bg-[#FFC107] text-[#202020] px-4 py-1.5 rounded text-sm font-semibold hover:bg-[#e6ad06] transition-colors disabled:opacity-50"
                >
                  {creatingVariants ? 'Generando...' : '⚡ Generar variantes'}
                </button>
              </div>

              {previewVariants.length > 0 && (
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm font-medium text-gray-700 mb-2">Se generarán {previewVariants.length} variantes:</p>
                  <div className="flex flex-wrap gap-1">
                    {previewVariants.map((v, i) => (
                      <span key={i} className="bg-white text-gray-700 text-xs px-2 py-1 rounded border">{v}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Variants management table */}
            {initialVariants.length > 0 && (
              <>
                {/* Search */}
                <div className="bg-white p-4 rounded-lg shadow">
                  <input type="text" placeholder="Buscar variantes..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]" />
                </div>

                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Variante</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Precio</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Activo</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Imagen</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {filtered.map((variant) => {
                          const isEditing = editingVariantId === variant.id
                          const variantImages = variant.variant_images || []

                          return (
                            <tr key={variant.id} className={isEditing ? 'bg-blue-50' : ''}>
                              <td className="px-4 py-3">
                                <span className={`font-medium ${!variant.active ? 'text-gray-400 line-through' : ''}`}>
                                  {variant.option_display}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-gray-500 font-mono text-xs">{variant.sku}</td>

                              <td className="px-4 py-3">
                                {isEditing ? (
                                  <input type="number" step="0.01" min="0" value={editPrice} onChange={(e) => setEditPrice(e.target.value)} className="w-24 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE] text-sm" />
                                ) : (
                                  <span className="font-mono">{formatCurrency(Number(variant.price))}</span>
                                )}
                              </td>

                              <td className="px-4 py-3">
                                {isEditing ? (
                                  <input type="number" min="0" value={editStock} onChange={(e) => setEditStock(e.target.value)} className="w-20 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE] text-sm" />
                                ) : (
                                  <span className={`px-2 py-1 rounded text-sm ${variant.stock_quantity < 10 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                                    {variant.stock_quantity}
                                  </span>
                                )}
                              </td>

                              <td className="px-4 py-3">
                                {isEditing ? (
                                  <label className="inline-flex items-center gap-1 cursor-pointer">
                                    <input type="checkbox" checked={editActive} onChange={(e) => setEditActive(e.target.checked)} className="accent-[#55ACEE] w-4 h-4" />
                                  </label>
                                ) : (
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${variant.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                    {variant.active ? 'Activo' : 'Inactivo'}
                                  </span>
                                )}
                              </td>

                              <td className="px-4 py-3">
                                {variantImages.length > 0 ? (
                                  <div className="flex items-center gap-1">
                                    <img src={variantImages[0].url} alt={variant.option_display} className="w-8 h-8 object-cover rounded border border-gray-200" />
                                    {variantImages.length > 1 && <span className="text-xs text-gray-400">+{variantImages.length - 1}</span>}
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-400">Sin imagen</span>
                                )}
                              </td>

                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  {isEditing ? (
                                    <>
                                      <button onClick={() => saveEdit(variant.id)} disabled={saving} className="bg-[#55ACEE] text-white px-2 py-1 rounded text-xs hover:bg-[#4a9dd4] disabled:opacity-50">
                                        {saving ? '...' : 'Guardar'}
                                      </button>
                                      <button onClick={cancelEdit} className="text-gray-500 hover:text-gray-700 text-xs">Cancelar</button>
                                    </>
                                  ) : (
                                    <button onClick={() => startEdit(variant)} className="text-[#55ACEE] hover:underline text-xs">Editar</button>
                                  )}

                                  <label className="text-[#FFC107] hover:underline text-xs cursor-pointer">
                                    {uploadingImageFor === variant.id ? '...' : '📷'}
                                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleAttachImages(variant.id, e.target.files)} disabled={uploadingImageFor === variant.id} />
                                  </label>

                                  <button onClick={() => handleDelete(variant.id)} disabled={deleting === variant.id} className="text-red-500 hover:underline text-xs disabled:opacity-50">
                                    {deleting === variant.id ? '...' : '🗑'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>

                  {filtered.length === 0 && search && (
                    <div className="p-8 text-center text-gray-400">No se encontraron variantes para "{search}"</div>
                  )}
                </div>

                {/* Image panels */}
                {filtered.map((variant) => {
                  const variantImages = variant.variant_images || []
                  if (variantImages.length === 0) return null

                  return (
                    <div key={variant.id} className="bg-white p-6 rounded-lg shadow">
                      <h3 className="text-lg font-semibold text-[#202020] mb-4">Imágenes: {variant.option_display} ({variantImages.length})</h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                        {variantImages.map((img) => (
                          <div key={img.id} className="relative group border border-gray-200 rounded-lg overflow-hidden">
                            <img src={img.url} alt={img.filename} className="w-full h-28 object-cover" />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                              <button onClick={() => handleDeleteImage(variant.id, img.id)} disabled={deletingImage === img.id} className="opacity-0 group-hover:opacity-100 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-all disabled:opacity-50">
                                {deletingImage === img.id ? (<span className="text-xs">...</span>) : (
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                )}
                              </button>
                            </div>
                            <div className="px-2 py-1 bg-gray-50 text-xs text-gray-500 truncate">{img.filename} · {formatBytes(img.byte_size)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </>
            )}

            {initialVariants.length === 0 && options.length > 0 && (
              <div className="bg-white p-8 rounded-lg shadow text-center">
                <p className="text-gray-500 text-lg mb-4">Tienes opciones configuradas pero aún no has generado las variantes.</p>
                <button type="button" onClick={handleGenerateVariants} disabled={creatingVariants} className="bg-[#FFC107] text-[#202020] px-6 py-2 rounded font-semibold hover:bg-[#e6ad06] transition-colors disabled:opacity-50">
                  {creatingVariants ? 'Generando...' : '⚡ Generar variantes ahora'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  )
}
